import { useEffect, useState } from "react";

import { SendPreviewModal } from "@/pages/buyers/sendPreview/SendPreviewModal";
import {
  usePrequalificationResendDraft,
  useResendPrequalificationDocuments,
} from "@/hooks/prequalification/usePrequalification";

/**
 * "Resend to applicant" — the send-preview for a prequalification completion
 * email, over the SAME modal the buyer sends use.
 *
 * <p>🔴 <b>The draft is composed SERVER-SIDE by the code path the send runs.</b>
 * Nothing here builds a subject, a body, or an attachment list: this component
 * renders what the server says would go, so a specialist reading the screen is
 * reading the message rather than a description of it. That is the
 * consumer-not-rebuild rule (D8), and it is the rule the buyer package modals
 * exist to demonstrate — a second composition in TypeScript is how a preview
 * comes to disagree with its own send.</p>
 *
 * <p><b>Blocked is rendered, not hidden.</b> When the server refuses — no
 * documents, a suppressed address, an oversized set — the modal still opens and
 * says why, because the reason is the only thing that tells the specialist what
 * to change. A hidden button would leave them with a record they can only
 * escalate.</p>
 */
export function PrequalificationResendModal({
  submissionId,
  onClose,
  onSent,
}: {
  submissionId: string;
  onClose: () => void;
  /** Fired with what actually went, so the page can say so beside the button. */
  onSent: (attachmentNames: string[]) => void;
}) {
  const draft = usePrequalificationResendDraft(submissionId, true);
  const resend = useResendPrequalificationDocuments(submissionId);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Seeded ONCE per load of the draft, keyed on the composed values rather than
  // on every render: a background refetch landing while the specialist is
  // halfway through an edit must not overwrite what they have typed. The buyer
  // package modals learned this the expensive way.
  const composedSubject = draft.data?.subject ?? "";
  const composedBody = draft.data?.body ?? "";
  useEffect(() => {
    setSubject(composedSubject);
    setBody(composedBody);
  }, [composedSubject, composedBody]);

  const blockedReason = draft.data?.blockedReason ?? null;
  const attachments = (draft.data?.attachmentNames ?? []).map((name) => ({ name }));

  const edited = subject !== composedSubject || body !== composedBody;

  return (
    <SendPreviewModal
      title="Resend to applicant"
      onClose={onClose}
      toName={draft.data?.toName ?? null}
      toEmail={draft.data?.toEmail ?? null}
      audienceNote="the applicant. It does not go to the referring partner — they received their copy when the application completed."
      // The completion email carries no Reply-To: it is composed by the module,
      // not by a person, so there is no individual for a reply to reach.
      repliesReachPhoenix
      subject={subject}
      onSubjectChange={setSubject}
      body={body}
      onBodyChange={setBody}
      attachments={attachments}
      replacesPreviewBody={
        draft.isLoading ? (
          <p className="text-small text-gray-mid m-0">Composing the email…</p>
        ) : draft.isError ? (
          <p className="text-small text-status-error m-0">
            The draft could not be loaded. Close this and try again.
          </p>
        ) : undefined
      }
      notices={
        <>
          {blockedReason && (
            <p
              className="text-small text-status-error m-0"
              role="alert"
              data-testid="prequal-resend-blocked"
            >
              {blockedReason}
            </p>
          )}
          {error && (
            <p className="text-small text-status-error m-0" role="alert">
              {error}
            </p>
          )}
        </>
      }
      footnote={
        <>
          The attachments are the documents already on file — this re-renders
          nothing. Use <strong>Regenerate documents</strong> first if they are
          missing or wrong.
        </>
      }
      confirmLabel="Send"
      confirmTestId="prequal-resend-confirm"
      confirmDisabled={!!blockedReason || draft.isLoading || draft.isError}
      confirmBlockedReason={blockedReason}
      pending={resend.isPending}
      onConfirm={() => {
        setError(null);
        resend.mutate(
          // Only an ACTUAL edit is sent. Echoing the composed strings back would
          // make every resend look edited in the activity row, and the row's
          // whole job is to record whether anyone changed the words.
          edited ? { subject, body } : undefined,
          {
            onSuccess: (r) => {
              onSent(r.attachmentNames);
              onClose();
            },
            onError: (e) =>
              setError(e instanceof Error ? e.message : "The email could not be sent."),
          },
        );
      }}
    />
  );
}
